import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom"; // Added useParams
import apiClient from "../../../api/apiClient";
import { toast } from "react-toastify";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import CustomEditor from "../../custom/CustomEditor";
import MultiSelectDropdown from "../MultiSelectDropdown";

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ["places"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MAX_IMAGES = 6;

/* ---------------- TOOLTIP INPUT ---------------- */
const Input = ({ error, tooltip, placeholder, value, ...props }) => (
  <div className={`floating-field position-relative ${value ? 'has-value' : ''}`} data-bs-toggle="tooltip" title={error || tooltip}>
    <input
      className={`form-control ${error ? "is-invalid" : ""}`}
      placeholder={placeholder}
      value={value}
      {...props}
    />
    <label className="floating-label">{placeholder}</label>
  </div>
);


/* ---------------- NORMALIZE DOCTOR ---------------- */
const normDoctor = (d) => ({
  id: d.id,
  name: d.name || d.doctor_name || `${d.first_name || ""} ${d.last_name || ""}`.trim(),
  city: d.city || d.location || "",
  speciality: d.speciality?.name || d.speciality_name || d.primary_speciality || "—",
  sub_speciality_name: d.sub_speciality_name || "—",
  speciality_id: d.speciality?.id || d.speciality_id || null,
});

export default function FormHospital() {
  const { id } = useParams(); // Get ID from URL for Edit mode
  const navigate = useNavigate();
  const autoRef = useRef(null);
  const isEditMode = Boolean(id); // Check if we are in Edit Mode

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries,
  });

  /* ================= STATE ================= */
  const [formData, setFormData] = useState({
    hospital_name: "",
    address: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
    consultation_fees: "",
    hospital_phone_number: "",
    hospital_whatsapp_number: "",
    services: "",
    availability: DAYS.map((d) => ({ day: d, open_time: "", close_time: "", is_closed: true })),
    doctor_ids: [], // array of {id, name, city, speciality, speciality_id}
    specialist_ids: [],
  });

  const [errors, setErrors] = useState({});
  const [activeAcc, setActiveAcc] = useState("h1");

  // Data sources
  const [doctors, setDoctors] = useState([]);
  const [specialityList, setSpecialityList] = useState([]); // [{id,name}]
  const [hospitalImages, setHospitalImages] = useState([]); // File[] (New uploads)
  const [existingImages, setExistingImages] = useState([]); // URLs from DB for Edit mode
  const [isDragOver, setIsDragOver] = useState(false);

  // Loading + error states for visibility issues
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [specsLoading, setSpecsLoading] = useState(false);
  const [doctorsError, setDoctorsError] = useState("");
  const [specsError, setSpecsError] = useState("");

  const [selectedSpecialities, setSelectedSpecialities] = useState([]);

  /* ================= LOAD DATA (SOURCES + PREFILL) ================= */
  useEffect(() => {
    const initializeForm = async () => {
      try {
        // 1. Load All Doctors first (needed to map names for the table)
        const docRes = await apiClient.get("/hospitals/getdoctors");
        const allDoctors = docRes?.data?.data || [];
        setDoctors(allDoctors);

        // 2. Load Specialities
        const specRes = await apiClient.get("/doctors/specialities-with-sub");
        const rows = specRes?.data?.data || [];
        const grouped = {};
        rows.forEach((r) => {
          const pId = r.speciality_id;
          const pName = r.speciality_name;
          const sId = r.sub_id;
          const sName = r.sub_speciality_name;
          if (pId && !grouped[pId]) {
            grouped[pId] = { id: pId, name: pName || `Category ${pId}`, subs: [] };
          }
          if (pId && sId) {
            grouped[pId].subs.push({ id: sId, name: sName || "Unnamed Sub-Speciality" });
          }
        });
        setSpecialityList(Object.values(grouped));

        // 3. IF EDIT MODE: Fetch Hospital Details
        if (isEditMode) {
          const hospRes = await apiClient.get(`/hospitals/${id}`);
          if (hospRes.data.success) {
            const h = hospRes.data.data;

            // Parse JSON strings from DB if necessary
            const parsedAvail = typeof h.availability === 'string' ? JSON.parse(h.availability) : h.availability;
            const parsedSpecs = typeof h.specialist_ids === 'string' ? JSON.parse(h.specialist_ids) : h.specialist_ids;

            // CRITICAL: Map saved Doctor IDs [1, 2] back to full objects {id:1, name:'Dr...'} for your UI table
            const savedDocIds = typeof h.doctor_ids === 'string' ? JSON.parse(h.doctor_ids) : (h.doctor_ids || []);
            const mappedDocs = allDoctors
              .filter(d => savedDocIds.includes(d.id))
              .map(d => normDoctor(d));

            setFormData({
              hospital_name: h.hospital_name || "",
              address: h.address || "",
              city: h.city || "",
              state: h.state || "",
              country: h.country || "India",
              pincode: h.pincode || "",
              consultation_fees: h.consultation_fees || "",
              hospital_phone_number: h.hospital_phone_number || "",
              hospital_whatsapp_number: h.hospital_whatsapp_number || "",
              services: h.services || "",
              availability: parsedAvail || formData.availability,
              doctor_ids: mappedDocs, // Table now shows names correctly
              specialist_ids: parsedSpecs || [],
            });

            // Set existing images for the gallery
            setExistingImages(h.hospital_images || []);
          }
        }
      } catch (err) {
        console.error("Initialization Error:", err);
        toast.error("Failed to load hospital data");
      }
    };

    initializeForm();
  }, [id, isEditMode]);
  // Update selected IDs directly in formData
  const handleSpecialityChange = (selectedIds) => {
    setFormData(prev => ({ ...prev, specialist_ids: selectedIds }));
  };

  /* ================= GOOGLE MAP ================= */
  const onPlaceChanged = () => {
    const place = autoRef.current?.getPlace();
    if (!place?.address_components) return;

    const get = (type) => place.address_components.find((c) => c.types.includes(type))?.long_name || "";

    setFormData((prev) => ({
      ...prev,
      address: place.formatted_address || "",
      city: get("locality") || get("administrative_area_level_2"),
      state: get("administrative_area_level_1"),
      country: get("country") || "India",
      pincode: get("postal_code") || "",
      hospital_name: prev.hospital_name || place.name || "",
    }));
  };

  /* ================= AVAILABILITY ================= */
  const toggleDay = (i, open) => {
    const arr = [...formData.availability];
    arr[i].is_closed = !open;
    if (!open) {
      arr[i].open_time = "";
      arr[i].close_time = "";
    }
    setFormData({ ...formData, availability: arr });
  };

  const updateTime = (i, key, val) => {
    const arr = [...formData.availability];
    arr[i][key] = val;
    setFormData({ ...formData, availability: arr });
  };

  /* ================= DOCTOR ================= */
  const addDoctorById = (id) => {
    const raw = doctors.find((x) => x.id === Number(id));
    if (!raw) return;
    const doc = normDoctor(raw);
    setFormData((prev) => {
      if (prev.doctor_ids.some((d) => d.id === doc.id)) return prev; // avoid dup
      return { ...prev, doctor_ids: [...prev.doctor_ids, doc] };
    });
  };

  const removeDoctor = (id) => {
    setFormData((prev) => ({ ...prev, doctor_ids: prev.doctor_ids.filter((d) => d.id !== id) }));
  };

  const setDoctorSpeciality = (doctorId, specialityId) => {
    setFormData((prev) => ({
      ...prev,
      doctor_ids: prev.doctor_ids.map((d) =>
        d.id === doctorId ? { ...d, speciality_id: Number(specialityId || 0) || null, speciality: findSpecName(specialityId) } : d
      ),
    }));
  };

  const findSpecName = (id) => specialityList.find((s) => s.id === Number(id))?.name || "—";

  /* ================= IMAGE UPLOADER ================= */
  const acceptImages = (files) => {
    const incoming = Array.from(files).filter((f) => /^image\//.test(f.type));
    let combined = [...hospitalImages, ...incoming];
    // Account for existing images in the limit
    if (combined.length + existingImages.length > MAX_IMAGES) {
      toast.warn(`Max ${MAX_IMAGES} total images allowed`);
      return;
    }
    setHospitalImages(combined);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    acceptImages(e.dataTransfer.files);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const onDragLeave = () => setIsDragOver(false);

  const removeImageAt = (idx) => {
    setHospitalImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const removeExistingImage = (imageId) => {
    setExistingImages(prev => prev.filter(img => img.id !== imageId));
  };

  /* ================= VALIDATION ================= */
  const validate = () => {
    const e = {};

    if (!formData.hospital_name.trim()) e.hospital_name = "Hospital name required";
    if (!formData.address.trim()) e.address = "Address required";
    if (!/^\d{6}$/.test(formData.pincode)) e.pincode = "Invalid pincode";
    if (!formData.consultation_fees) e.consultation_fees = "Fees required";
    if (!/^\d{10}$/.test(formData.hospital_phone_number)) e.hospital_phone_number = "Invalid phone";
    if (!/^\d{10}$/.test(formData.hospital_whatsapp_number)) e.hospital_whatsapp_number = "Invalid WhatsApp";
    if (!formData.services) e.services = "Services required";

    // Check total images in edit mode
    if (!hospitalImages.length && !existingImages.length) e.images = "Upload hospital images";

    const validDay = formData.availability.some((d) => !d.is_closed && d.open_time && d.close_time);
    if (!validDay) e.availability = "Set availability";

    if (!formData.doctor_ids.length && !formData.specialist_ids.length) e.doctors = "Select doctor or speciality";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const fd = new FormData();

    // append base fields
    Object.entries(formData).forEach(([k, v]) => {
      fd.append(k, Array.isArray(v) || typeof v === 'object' ? JSON.stringify(v) : v);
    });

    fd.set("doctor_ids", JSON.stringify(formData.doctor_ids.map((d) => d.id)));

    // Add new images
    hospitalImages.forEach((img) => fd.append("hospital_images", img));

    // Send existing images IDs to KEEP
    fd.append("keep_images", JSON.stringify(existingImages.map(img => img.id)));

    try {
      if (isEditMode) {
        await apiClient.post(`/hospitals/update/${id}`, fd);
        toast.success("Hospital updated successfully");
      } else {
        await apiClient.post("/hospitals", fd);
        toast.success("Hospital saved successfully");
      }
      navigate("/admin/medical-tourism-hospitals");
    } catch (error) {
      const serverMsg = error.response?.data?.message;
      toast.error(serverMsg || "Failed to save hospital");
    }
  };

  /* ================= ACCORDION HEADER STATUS ================= */
  const hasErrorsIn = (sectionKey) => {
    switch (sectionKey) {
      case "h1":
        return !!(
          errors.hospital_name ||
          errors.address ||
          errors.consultation_fees ||
          errors.hospital_phone_number ||
          errors.hospital_whatsapp_number ||
          errors.pincode
        );
      case "h2":
        return !!errors.availability;
      case "h3":
        return !!errors.doctors;
      case "h4":
        return !!errors.images;
      case "h5":
        return !!errors.services;
      default:
        return false;
    }
  };

  const HeaderButton = ({ id, children }) => (
    <button
      className={`accordion-button d-flex align-items-center justify-content-between ${hasErrorsIn(id) ? "text-danger" : ""} ${activeAcc === id ? "" : "collapsed"}`}
      type="button"
      onClick={() => setActiveAcc(activeAcc === id ? "" : id)}
      title={hasErrorsIn(id) ? "Please resolve errors in this section" : ""}
    >
      <span>{children}</span>
      {hasErrorsIn(id) && <span className="badge bg-danger ms-2">Fix</span>}
    </button>
  );

  /* ================= UI ================= */
  return (
    <form onSubmit={handleSubmit}>
      <div className="accordion">
        {/* HOSPITAL DETAILS */}
        <div className="accordion-item">
          <HeaderButton id="h1">Hospital Details *</HeaderButton>
          {activeAcc === "h1" && (
            <div className="accordion-body row g-3">
              <div className="col-md-12">
                {isLoaded && (
                  <Autocomplete onLoad={(r) => (autoRef.current = r)} onPlaceChanged={onPlaceChanged}>
                    <Input
                      placeholder="Search address *"
                      value={formData.address}
                      error={errors.address}
                      tooltip="Search hospital address"
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    />
                  </Autocomplete>
                )}
              </div>

              <div className="col-md-12">
                <Input
                  placeholder="Hospital Name *"
                  value={formData.hospital_name}
                  error={errors.hospital_name}
                  tooltip="Hospital name"
                  onChange={(e) => setFormData({ ...formData, hospital_name: e.target.value })}
                />
              </div>

              <div className="col-md-3">
                <Input
                  placeholder="Fees *"
                  value={formData.consultation_fees}
                  error={errors.consultation_fees}
                  onChange={(e) => setFormData({ ...formData, consultation_fees: e.target.value })}
                />
              </div>

              <div className="col-md-3">
                <Input
                  placeholder="Phone *"
                  value={formData.hospital_phone_number}
                  error={errors.hospital_phone_number}
                  onChange={(e) => setFormData({ ...formData, hospital_phone_number: e.target.value })}
                />
              </div>

              <div className="col-md-3">
                <Input
                  placeholder="WhatsApp *"
                  value={formData.hospital_whatsapp_number}
                  error={errors.hospital_whatsapp_number}
                  onChange={(e) => setFormData({ ...formData, hospital_whatsapp_number: e.target.value })}
                />
              </div>

              <div className="col-md-3">
                <Input
                  placeholder="Pincode *"
                  value={formData.pincode}
                  error={errors.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>

        {/* AVAILABILITY */}
        <div className="accordion-item">
          <HeaderButton id="h2">Doctor Availability *</HeaderButton>
          {activeAcc === "h2" && (
            <div className="accordion-body">
              {formData.availability.map((d, i) => (
                <div className="row mb-2" key={i}>
                  <div className="col-md-2">{d.day}</div>
                  <div className="col-md-2">
                    <input type="checkbox" checked={!d.is_closed} onChange={(e) => toggleDay(i, e.target.checked)} /> Open
                  </div>
                  {!d.is_closed && (
                    <>
                      <div className="col-md-3">
                        <input type="time" className="form-control" value={d.open_time} onChange={(e) => updateTime(i, "open_time", e.target.value)} />
                      </div>
                      <div className="col-md-3">
                        <input type="time" className="form-control" value={d.close_time} onChange={(e) => updateTime(i, "close_time", e.target.value)} />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* DOCTORS & SPECIALITIES */}
        <div className="accordion-item">
          <HeaderButton id="h3">Doctors & Specialities *</HeaderButton>
          {activeAcc === "h3" && (
            <div className="accordion-body">
              {/* Error or loading states for visibility issues */}
              {doctorsLoading && <div className="alert alert-secondary">Loading doctors…</div>}

              <div className="floating-field mb-3">
                <select
                  className="form-select"
                  onChange={(e) => {
                    addDoctorById(e.target.value);
                    e.target.value = "";
                  }}
                  title={errors.doctors || "Select doctor"}
                  disabled={doctorsLoading || doctors.length === 0}
                >
                  <option value="">{doctors.length ? "Select Doctor" : "No doctors available"}</option>
                  {doctors.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name || d.doctor_name}
                    </option>
                  ))}
                </select>
                <label className="floating-label">Select Doctor</label>
              </div>

              {/* Selected doctors table + speciality selector */}
              {formData.doctor_ids.length > 0 && (
                <div className="table-responsive mb-3">
                  <table className="table table-striped align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: "30%" }}>Doctor Name</th>
                        <th style={{ width: "25%" }}>Speciality</th>
                        <th style={{ width: "25%" }}>City</th>
                        <th style={{ width: "20%" }} className="text-end">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.doctor_ids.map((d) => (
                        <tr key={d.id}>
                          <td>{d.name}</td>
                          <td className="text-muted">{d.sub_speciality_name}</td>
                          <td className="text-muted">{d.city}</td>
                          <td className="text-end">
                            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeDoctor(d.id)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Hospital-level speciality multi-select (unchanged) */}
              <MultiSelectDropdown
                options={specialityList}
                selectedValues={formData.specialist_ids}
                onChange={handleSpecialityChange}
              />
            </div>
          )}
        </div>

        {/* IMAGES */}
        <div className="accordion-item">
          <HeaderButton id="h4">Hospital Images *</HeaderButton>
          {activeAcc === "h4" && (
            <div className="accordion-body">
              <div className="alert alert-info py-2 mb-3 small">Note: Up to {MAX_IMAGES} total images.</div>

              {/* Display Existing Images (Edit mode) */}
              {existingImages.length > 0 && (
                <div className="mb-3">
                  <label className="form-label small fw-bold">Current Photos</label>
                  <div className="row g-2">
                    {existingImages.map((img) => (
                      <div className="col-6 col-md-3" key={img.id}>
                        <div className="position-relative border rounded overflow-hidden">
                          <img src={img.image_url} alt="" style={{ width: "100%", height: 120, objectFit: "cover" }} />
                          <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-0" onClick={() => removeExistingImage(img.id)}>
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div
                className={`border rounded d-flex flex-column align-items-center justify-content-center p-4 mb-3 ${isDragOver ? "bg-light" : ""}`}
                style={{ minHeight: 120, cursor: "pointer", border: "2px dashed #ced4da" }}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={() => document.getElementById("hospital-image-input").click()}
                title={errors.images || "Drop or click to upload"}
              >
                <div className="display-6">+</div>
                <div className="text-muted small">Drop new images here or click to upload</div>
              </div>
              <input id="hospital-image-input" type="file" name="hospital_images" multiple accept="image/*" className="d-none" onChange={(e) => acceptImages(e.target.files)} />

              {hospitalImages.length > 0 && (
                <div className="row g-2">
                  {hospitalImages.map((file, idx) => (
                    <div className="col-6 col-md-3" key={idx}>
                      <div className="position-relative border border-primary rounded overflow-hidden">
                        <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: "100%", height: 120, objectFit: "cover" }} />
                        <button type="button" className="btn btn-sm btn-primary position-absolute top-0 end-0 rounded-0" onClick={() => removeImageAt(idx)}>
                          ×
                        </button>
                      </div>
                      <div className="small mt-1 text-truncate text-muted" style={{ fontSize: '11px' }}>{file.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* SERVICES */}
        <div className="accordion-item">
          <HeaderButton id="h5">Services *</HeaderButton>
          {activeAcc === "h5" && (
            <div className="accordion-body">
              <CustomEditor data={formData.services} onChange={(v) => setFormData({ ...formData, services: v })} showHtmlMode={false} />
            </div>
          )}
        </div>
      </div>

      <div className="text-end mt-3 mb-5">
        <button className="btn btn-success px-5">{isEditMode ? "Update Hospital" : "Save Hospital"}</button>
      </div>
    </form>
  );
}